import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { CustodyEvent } from "../../../models/CustodyEvent";
import { useCustodyEvent } from "../../../contexts/CustodyEventContext";
import { useMemo } from "react";
import { useMatter } from "../../../contexts/MatterContext";
import tinycolor from "tinycolor2"; // Add this package for color manipulation
import { useCustodyEventDialog } from "../../../providers/CustodyEventDialogProvider";

const localizer = momentLocalizer(moment);

const transformEvents = (events: CustodyEvent[]) => {
  return events.map((event) => {
    let start = moment(`${event.startDate}T${event.startTime}`);
    let end = moment(`${event.endDate}T${event.endTime}`);
    // Ensure end is after start if they're equal
    if (start.isSame(end)) end = end.clone().add(1, "minute");
    return {
      id: event._id,
      title: event.title,
      start: start.toDate(),
      end: end.toDate(),
      allDay: false,
      resource: event,
    };
  });
};

const getHolidayStyle = (base: string) => {
  const light = tinycolor(base).lighten(40).toHexString();
  const dark = tinycolor(base).setAlpha(0.25).toRgbString(); // subtle transparent overlay
  return {
    backgroundImage: `
      repeating-linear-gradient(
        45deg,
        ${dark} 0px,
        ${dark} 4px,
        transparent 4px,
        transparent 8px
      )`,
    backgroundColor: light,
  };
};

const CustodyCalendar = () => {
  const { custodyEvents } = useCustodyEvent();
  const { openDialog } = useCustodyEventDialog();
  const { partiesInvolved } = useMatter();
  const events = transformEvents(custodyEvents);

  const participantColors = useMemo(() => {
    const palette = ["#3B82F6", "#EC4899", "#10B981", "#8B5CF6"]; // Tailwind-like hex codes
    return partiesInvolved.reduce(
      (acc, p, i) => {
        acc[p._id] = palette[i % palette.length];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [partiesInvolved]);

  return (
    <div className="h-[80vh] p-4">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: "100%" }}
        defaultView={Views.WEEK}
        views={[Views.WEEK, Views.MONTH]}
        showMultiDayTimes={true}
        scrollToTime={moment().toDate()}
        onDoubleClickEvent={(event) => openDialog(event.resource)}
        eventPropGetter={(event) => {
          const participantId = event.resource?.participantId;
          const baseColor = participantColors[participantId] || "#6B7280";

          if (event.resource?.eventType === "holiday") {
            const holidayStyle = getHolidayStyle(baseColor);
            return {
              style: {
                ...holidayStyle,
                borderRadius: "6px",
                color: "black",
                fontWeight: "bold",
                border: "1px dashed white",
                display: "block",
                padding: "2px 6px",
              },
            };
          }

          return {
            style: {
              backgroundColor: baseColor,
              borderRadius: "6px",
              color: "white",
              border: 0,
              display: "block",
              padding: "2px 6px",
            },
          };
        }}
      />
    </div>
  );
};

export default CustodyCalendar;
